import * as Sentry from "@sentry/react";

import { Form, FormikProps } from "formik";

import { MarketingConsent } from "Components/Consent/MarketingConsent";
import { ProductIntroTile } from "Components/Features/CheckPrice/Tiles/ProductIntroTile";
import { PartnerProductIntroForms } from "Components/Features/CheckPrice/Tiles/ProductIntroTile/PartnerProductIntroForms/PartnerProductIntroForms";
import { pepValidationSchema as validationSchema } from "Components/Form/CommonFormElements/CitizenshipPepFormElement";
import { Pep } from "Components/Form/CommonFormElements/CitizenshipPepFormElement/Pep";
import { ErrorsSummedUp } from "Components/Form/ErrorsSummedUp";
import { InputFailureTracker } from "Components/InputFailureTracker";
import { NavigationButtons } from "Components/NavigationButtons";
import { ScrollToFirstError } from "Components/ScrollToFirstError";
import { getCollectionOfDittoComponents } from "Components/TextContext";
import { TextComponentNameLevel1 } from "Components/TextContext/textConstants";
import { IllustrationName } from "Constants/constants";
import { ProductBackendName } from "Features/CheckPrice/Products/productTypes";
import { useIsAdvisorSelector } from "Store/advisor/advisorSelectors";
import { useCurrentCart } from "Store/cart";
import { INSURANCE_NAME_VALUE } from "Store/interfaces/products";
import { useFormState } from "Store/priceCalculation/priceCalculationHooks";
import { updatePriceCalculationFormState } from "Store/priceCalculation/priceCalculationSlice";
import { nextStep } from "Store/steps/stepsSlice";
import { mapValidationErrorsDitto } from "Utils/formUtils";
import React from "react";
import { useDispatch } from "react-redux";
import { AKA_CHILD_ABOUT_YOU_STEP } from "..";
import { AkaChildProductIntroFormValues } from "./akaChildProductIntroInterfaces";
import { mapAkaChildAdviceToForm } from "./akaChildProductIntroUtils";

export const AkaChildProductIntro = (): React.ReactElement => {
  const isAdvisor = useIsAdvisorSelector();
  const dispatch = useDispatch();
  const adviceProductValues = useCurrentCart()?.advice?.adviceProductValues;
  const initialFormState = useFormState<AkaChildProductIntroFormValues>(
    AKA_CHILD_ABOUT_YOU_STEP.id,
    mapAkaChildAdviceToForm(adviceProductValues),
  );

  const { shorttitle: shortTitleComponents } = getCollectionOfDittoComponents(
    TextComponentNameLevel1.AKA_CHILD,
  );

  const handleSubmit = (form: AkaChildProductIntroFormValues): void => {
    dispatch(
      updatePriceCalculationFormState({
        stepId: AKA_CHILD_ABOUT_YOU_STEP.id,
        formState: form,
      }),
    );
    dispatch(nextStep());
  };

  return (
    <Sentry.ErrorBoundary
      beforeCapture={(scope) => {
        scope.setTag(
          "location",
          `CheckPrice - ProductIntro - ${INSURANCE_NAME_VALUE.AKA_CHILD}`,
        );
      }}
    >
      <ProductIntroTile
        illustrationName={IllustrationName.ESTABLISHMENT_FAMILY}
        productBackendName={ProductBackendName.AKA_CHILD}
        validationSchema={validationSchema()}
        initialValues={initialFormState}
        onSubmit={handleSubmit}
      >
        {(formProps: FormikProps<AkaChildProductIntroFormValues>) => {
          return (
            <Form>
              <Pep formProps={formProps} />
              <PartnerProductIntroForms />
              {!isAdvisor && <MarketingConsent />}
              <ScrollToFirstError />
              <ErrorsSummedUp
                errors={mapValidationErrorsDitto(
                  formProps.errors,
                  shortTitleComponents,
                )}
                isVisible
              />
              <NavigationButtons hidePreviousButton />
              <InputFailureTracker
                productName={INSURANCE_NAME_VALUE.AKA_CHILD}
                stepId={AKA_CHILD_ABOUT_YOU_STEP.id}
              />
            </Form>
          );
        }}
      </ProductIntroTile>
    </Sentry.ErrorBoundary>
  );
};
